import { useContext } from 'react';
import {
  Box,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useQuery } from '@tanstack/react-query';

import { AppContext } from '../../../App';
import { DocumentDispatchContext } from '../../../context/DocumentContext';
import { IDocumentRelationshipEntry, IDocumentUnscoped } from '../../../types/my-types';
import { formatExclusionReason } from '../../../types/packager-enum-descriptions';
import { DocumentDataStatusFilters } from '../../../types/packager-types';
import { LinkTableStyled } from '../../common/StyledComponents';
import DocumentDataStatus from '../../extractionBatches/details/DocumentDataStatus';
import useGlynt from '../../hooks/useGlynt';
import { SectionContainer, TypographySmallTitle } from './DocumentDetailsLeft';
import SvgEyeIcon from '../../../images/icons/eye_icon.svg?react';

interface DocumentRelationshipsSectionProps {
  documentData: IDocumentUnscoped;
}

const DocumentRelationshipsSection = ({ documentData }: DocumentRelationshipsSectionProps) => {
  const { setToast } = useContext(AppContext);
  const dispatch = useContext<React.Dispatch<any>>(DocumentDispatchContext);
  const parent = documentData.relationships?.split?.parent;
  const children = documentData.relationships?.split?.children ?? [];
  const relatedIds = [parent?.document_id, ...children.map((child) => child.document_id)].filter(
    (id): id is string => !!id
  );
  const glynt = useGlynt(documentData.data_pool_id);

  const { data: relatedDocuments, isFetching } = useQuery({
    queryKey: ['document-relationships', documentData.data_pool_id, documentData.id, relatedIds],
    queryFn: async () =>
      (await glynt).document.listUnscoped({
        id: relatedIds.join(','),
        limit: relatedIds.length,
      }),
    enabled: relatedIds.length > 0,
    staleTime: 30_000,
  });

  const copyDocumentId = (documentId: string) => {
    navigator.clipboard.writeText(documentId);
    setToast({
      open: true,
      message: 'Copied!',
      variant: 'success',
    });
  };

  const copyIconSx = {
    fontSize: '14px',
    cursor: 'pointer',
    color: '#5f6d7e',
  };

  const viewDocument = (relatedDocument?: IDocumentUnscoped) => {
    if (!relatedDocument) return;
    dispatch({
      type: 'TOGGLE_VIEW_DOCUMENT_MODAL',
      pages: relatedDocument.pages,
      id: relatedDocument.id,
    });
  };

  const relationshipTableContainerSx = {
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: '6px',
    overflow: 'auto',
    backgroundColor: '#fff',
    mt: 0.75,
    maxHeight: '280px',
  };

  const relationshipTableSx = {
    minWidth: 680,
    tableLayout: 'fixed',
    '& .MuiTableCell-root': {
      borderBottom: '1px solid rgba(0,0,0,0.05)',
      px: 1,
      py: 0.75,
      fontSize: '12px',
      lineHeight: 1.25,
    },
    '& .MuiTableRow-root:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '& .MuiTableRow-root:hover': {
      backgroundColor: 'rgba(102,187,169,0.04)',
    },
  };

  const relationshipTableHeadSx = {
    '& .MuiTableCell-head': {
      backgroundColor: 'rgba(0,0,0,0.025)',
      color: '#6b7280',
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.35px',
      textTransform: 'uppercase',
      py: 0.75,
    },
  };

  const getDocumentDataStatusReason = (
    relatedDocument?: Pick<
      IDocumentUnscoped,
      'document_data_status' | 'exclusion_reason' | 'escalation_detail'
    >
  ) => {
    if (
      relatedDocument?.document_data_status === DocumentDataStatusFilters.EXCLUDED &&
      relatedDocument.exclusion_reason
    ) {
      return formatExclusionReason(relatedDocument.exclusion_reason);
    }

    if (
      relatedDocument?.document_data_status === DocumentDataStatusFilters.ESCALATED &&
      relatedDocument.escalation_detail
    ) {
      return relatedDocument.escalation_detail;
    }

    return null;
  };

  const renderDocumentDataStatus = (
    relatedDocument?: Pick<
      IDocumentUnscoped,
      'document_data_status' | 'exclusion_reason' | 'escalation_detail'
    >
  ) => {
    const reason = getDocumentDataStatusReason(relatedDocument);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <DocumentDataStatus status={relatedDocument?.document_data_status} />
        {reason && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {reason}
          </Typography>
        )}
      </Box>
    );
  };

  const renderRelationship = (relationship: IDocumentRelationshipEntry) => {
    const relatedDocument = relatedDocuments?.results.find(
      (result) => result.id === relationship.document_id
    );

    return (
      <Box
        key={relationship.document_id}
        sx={{
          p: 1,
          border: '1px solid rgba(24,119,135,0.25)',
          borderRadius: '6px',
          backgroundColor: 'rgba(102,187,169,0.06)',
        }}
      >
        <LinkTableStyled
          to={`/data-pools/${relationship.data_pool_id}/documents/${relationship.document_id}`}
          data-cy={`link-related-document-${relationship.document_id}`}
        >
          {relatedDocument
            ? `${relatedDocument.label}${relatedDocument.file_extension || ''}`
            : relationship.document_id}
        </LinkTableStyled>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Typography variant="caption" color="textSecondary">
            ID: {relationship.document_id}
          </Typography>
          <ContentCopyIcon
            sx={copyIconSx}
            data-cy={`copy-related-document-id-${relationship.document_id}`}
            onClick={(event) => {
              event.stopPropagation();
              copyDocumentId(relationship.document_id);
            }}
          />
          {relatedDocument && (
            <Typography variant="caption" color="textSecondary">
              · {relatedDocument.pages.length} page{relatedDocument.pages.length === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <SectionContainer data-cy="document-relationships-section">
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <TypographySmallTitle>DOCUMENT RELATIONSHIPS</TypographySmallTitle>
        {isFetching && <CircularProgress size={12} />}
      </Stack>

      {parent && (
        <Box sx={{ mb: children.length > 0 ? 1.5 : 0 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>
            PARENT
          </Typography>
          {renderRelationship(parent)}
        </Box>
      )}

      {children.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
            CHILDREN ({children.length})
          </Typography>
          <Box sx={relationshipTableContainerSx}>
            <Table sx={relationshipTableSx} size="small">
              <TableHead sx={relationshipTableHeadSx}>
                <TableRow>
                  <TableCell sx={{ width: 220 }}>Name</TableCell>
                  <TableCell sx={{ width: 110 }}>ID</TableCell>
                  <TableCell sx={{ width: 170 }}>Data Status</TableCell>
                  <TableCell align="right" sx={{ width: 70 }}>
                    Pages
                  </TableCell>
                  <TableCell align="right" sx={{ width: 60 }}>
                    View
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}
                {!isFetching &&
                  children.map((child) => {
                    const childDocument = relatedDocuments?.results.find(
                      (result) => result.id === child.document_id
                    );

                    return (
                      <TableRow key={child.document_id}>
                        <TableCell>
                          <LinkTableStyled
                            to={`/data-pools/${child.data_pool_id}/documents/${child.document_id}`}
                            data-cy={`link-child-document-${child.document_id}`}
                            style={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {childDocument
                              ? `${childDocument.label}${childDocument.file_extension || ''}`
                              : 'Loading...'}
                          </LinkTableStyled>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{child.document_id}</span>
                            <ContentCopyIcon
                              sx={copyIconSx}
                              data-cy={`copy-child-document-id-${child.document_id}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                copyDocumentId(child.document_id);
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {renderDocumentDataStatus(childDocument)}
                        </TableCell>
                        <TableCell align="right">{childDocument?.pages.length ?? '—'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={childDocument ? 'View Document' : 'Document unavailable'}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <SvgEyeIcon
                                data-cy={`icon-view-child-document-${child.document_id}`}
                                style={{
                                  maxHeight: '16px',
                                  color: '#66BBA9',
                                  cursor: childDocument ? 'pointer' : 'default',
                                  opacity: childDocument ? 1 : 0.35,
                                  pointerEvents: childDocument ? 'auto' : 'none',
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  viewDocument(childDocument);
                                }}
                              />
                            </Box>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </SectionContainer>
  );
};

export default DocumentRelationshipsSection;
